import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Landing-page social-proof carousel.
 *
 * The backend now returns real personas only when there is meaningful proof to
 * show. If none exists, the UI renders a neutral empty state rather than a
 * synthetic sample tile.
 */

function fmtMoney(n) {
  const v = Number(n) || 0;
  const sign = v >= 0 ? "+" : "-";
  return `${sign}$${Math.abs(v).toLocaleString("en-US", { maximumFractionDigits: 0 })}`;
}

/* Tiny inline SVG sparkline. width 220, height 56. Auto-fits to data range
 * with a 6 % vertical pad so the curve never clips against tile edges. */
function Sparkline({ data, color = "#4ade80", w = 220, h = 56 }) {
  const id = useMemo(() => `sparkfill_${Math.random().toString(36).slice(2, 8)}`, []);
  if (!Array.isArray(data) || data.length < 2) return null;
  const min = Math.min(...data);
  const max = Math.max(...data);
  const span = Math.max(max - min, 1);
  const padY = h * 0.06;
  const usable = h - padY * 2;
  const points = data.map((v, i) => {
    const x = (i / (data.length - 1)) * w;
    const y = padY + (1 - (v - min) / span) * usable;
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(" ");
  // Filled area path
  const areaPts = `0,${h} ${points} ${w},${h}`;
  return (
    <svg width="100%" viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" style={{ display: "block" }}>
      <defs>
        <linearGradient id={id} x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.45" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <polygon points={areaPts} fill={`url(#${id})`} />
      <polyline points={points} fill="none" stroke={color} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  );
}

export default function PublicPersonas() {
  const [personas, setPersonas] = useState([]);
  const [usingSample, setUsingSample] = useState(false);
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  const ivRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/public/personas`).then(r => {
      if (cancelled) return;
      const list = Array.isArray(r.data?.personas) ? r.data.personas : [];
      setPersonas(list);
      setUsingSample(typeof r.data?.usingSample === "boolean" ? r.data.usingSample : false);
    }).catch(() => {
      setPersonas([]);
      setUsingSample(false);
    });
    return () => { cancelled = true; };
  }, []);

  // Auto-rotate every 4.5s unless hovered/touched.
  useEffect(() => {
    if (paused) return undefined;
    if (personas.length <= 1) return undefined;
    ivRef.current = setInterval(() => {
      setActive(i => (i + 1) % personas.length);
    }, 4500);
    return () => clearInterval(ivRef.current);
  }, [paused, personas.length]);

  const p = personas[active] || null;
  const isPositive = (p?.weeklyPnl ?? 0) >= 0;
  const sparkColor = isPositive ? "#4ade80" : "#f87171";
  const realCount = personas.filter(x => x.sample === false).length;

  if (!p) {
    return (
      <section
        data-testid="public-personas"
        style={{ padding: "20px 24px 60px", maxWidth: 1100, margin: "0 auto", width: "100%", boxSizing: "border-box" }}
      >
        <div style={{ fontSize: 11, color: "#60a5fa", letterSpacing: 2, textAlign: "center", fontWeight: 700, marginBottom: 8 }}>
          NO PUBLIC PROOF YET
        </div>
        <h2 style={{ fontSize: 28, fontWeight: 800, textAlign: "center", marginBottom: 6, letterSpacing: -0.5 }}>
          Community proof will appear here once real opt-ins are available.
        </h2>
        <p style={{ fontSize: 13, color: "#8b949e", textAlign: "center", marginBottom: 26 }}>
          There is no live digest or opted-in persona to display right now.
        </p>
      </section>
    );
  }

  return (
    <section
      data-testid="public-personas"
      style={{ padding: "20px 24px 60px", maxWidth: 1100, margin: "0 auto", width: "100%", boxSizing: "border-box" }}
    >
      <div style={{ fontSize: 11, color: "#60a5fa", letterSpacing: 2, textAlign: "center", fontWeight: 700, marginBottom: 8 }}>
        {usingSample ? "ILLUSTRATIVE EXAMPLE" : "SOCIAL PROOF"}
      </div>
      <h2 style={{ fontSize: 28, fontWeight: 800, textAlign: "center", marginBottom: 6, letterSpacing: -0.5 }}>
        {realCount > 0
          ? `Join ${realCount} trader${realCount === 1 ? "" : "s"} — here's what their week looked like`
          : "Here's what a tracked week looked like"}
      </h2>
      <p style={{ fontSize: 13, color: "#8b949e", textAlign: "center", marginBottom: 26 }}>
        {usingSample
          ? "These are illustrative sample numbers, not real trader results — nobody has opted into the community board yet."
          : "Anonymized weekly digests from real opted-in Omega Pilot users."}
      </p>

      <div
        data-testid="personas-carousel"
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
        onTouchStart={() => setPaused(true)}
        onTouchEnd={() => setPaused(false)}
        style={{
          maxWidth: 560, margin: "0 auto", padding: 22, borderRadius: 20,
          background: "linear-gradient(155deg, rgba(29,78,216,0.10), rgba(16,185,129,0.06))",
          border: "1px solid rgba(255,255,255,0.08)",
          boxShadow: "0 20px 60px rgba(2,6,23,0.45)",
          backdropFilter: "blur(12px)",
          position: "relative", overflow: "hidden",
        }}
      >
        {/* Avatar + name row */}
        <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 14 }}>
          <div
            data-testid="persona-avatar"
            style={{
              width: 52, height: 52, borderRadius: 14, flex: "0 0 auto",
              background: `linear-gradient(135deg, ${p.gradient?.[0] || "#3b82f6"}, ${p.gradient?.[1] || "#1d4ed8"})`,
              display: "grid", placeItems: "center",
              fontSize: 18, fontWeight: 800, color: "#fff",
              boxShadow: "0 8px 24px rgba(59,130,246,0.30)",
              letterSpacing: 0.5,
            }}
          >{p.initials}</div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div data-testid="persona-name" style={{ fontSize: 15, fontWeight: 700, color: "#e6edf3", marginBottom: 2, display: "flex", alignItems: "center", gap: 6 }}>
              {p.name}
              {p.isLive && (
                <span data-testid="persona-live-badge" style={{
                  fontSize: 8, fontWeight: 800, letterSpacing: 1.2, padding: "2px 6px",
                  borderRadius: 4, background: "rgba(74,222,128,0.18)", color: "#4ade80",
                  border: "1px solid rgba(74,222,128,0.4)",
                }}>LIVE</span>
              )}
              {p.sample !== false && (
                <span data-testid="persona-sample-badge" style={{
                  fontSize: 8, fontWeight: 800, letterSpacing: 1.2, padding: "2px 6px",
                  borderRadius: 4, background: "rgba(148,163,184,0.18)", color: "#94a3b8",
                  border: "1px solid rgba(148,163,184,0.4)",
                }}>SAMPLE</span>
              )}
            </div>
            <div style={{ fontSize: 12, color: "#8b949e", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.tagline}</div>
          </div>
          <div style={{ textAlign: "right", flex: "0 0 auto" }}>
            <div data-testid="persona-pnl" style={{ fontSize: 22, fontWeight: 800, color: isPositive ? "#4ade80" : "#f87171", letterSpacing: -0.5 }}>
              {fmtMoney(p.weeklyPnl)}
            </div>
            <div style={{ fontSize: 11, color: isPositive ? "#4ade80" : "#f87171", opacity: 0.85 }}>
              {isPositive ? "+" : ""}{(p.weeklyPnlPct ?? 0).toFixed(1)}% this week
            </div>
          </div>
        </div>

        {/* Sparkline */}
        <div style={{ marginBottom: 12, padding: "6px 4px", borderRadius: 10, background: "rgba(0,0,0,0.18)" }}>
          <Sparkline data={p.spark || []} color={sparkColor} />
        </div>

        {/* Stat pills */}
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 12 }}>
          <div style={pillStyle}>
            <span style={pillLabelStyle}>Trades</span>
            <span style={pillValueStyle}>{p.trades ?? 0}</span>
          </div>
          <div style={pillStyle}>
            <span style={pillLabelStyle}>Win rate</span>
            <span style={pillValueStyle}>{p.winRate ?? 0}%</span>
          </div>
          <div style={pillStyle}>
            <span style={pillLabelStyle}>Avg Zi</span>
            <span style={pillValueStyle}>{p.ziAvg ?? "—"}</span>
          </div>
        </div>

        {/* Top trade caption */}
        <div data-testid="persona-top-trade" style={{
          fontSize: 12, color: "#8b949e", padding: "10px 12px", borderRadius: 10,
          background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.05)",
        }}>
          <span style={{ color: "#60a5fa", fontWeight: 700, marginRight: 6, letterSpacing: 1, fontSize: 10 }}>TOP TRADE</span>
          {p.topTrade}
        </div>
      </div>

      {/* Pager dots */}
      <div data-testid="personas-pager" style={{ display: "flex", gap: 6, justifyContent: "center", marginTop: 18 }}>
        {personas.map((_, i) => (
          <button
            key={i}
            aria-label={`View persona ${i + 1}`}
            data-testid={`persona-dot-${i}`}
            onClick={() => setActive(i)}
            style={{
              width: i === active ? 22 : 8, height: 8, borderRadius: 999, border: "none", padding: 0,
              background: i === active ? "linear-gradient(90deg, #60a5fa, #4ade80)" : "rgba(255,255,255,0.18)",
              cursor: "pointer", transition: "width 220ms ease, background 220ms ease",
            }}
          />
        ))}
      </div>
    </section>
  );
}

const pillStyle = {
  display: "inline-flex", alignItems: "center", gap: 6,
  padding: "5px 10px", borderRadius: 999,
  background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)",
};
const pillLabelStyle = { fontSize: 10, color: "#8b949e", letterSpacing: 1 };
const pillValueStyle = { fontSize: 12, fontWeight: 700, color: "#e6edf3" };
